<template>
  <div class="home-page">
    <header class="hero-shell">
      <div class="hero-nav">
        <a class="brand" href="/">
          <span class="brand-mark">FG</span>
          <span class="brand-copy">
            <strong>FreeGame</strong>
            <em>Black Arcade Selection</em>
          </span>
        </a>
        <div class="hero-nav-meta">
          <span>{{ featuredLabel }}</span>
          <span>{{ gameCount }} games</span>
        </div>
      </div>

      <section class="hero-grid">
        <div class="hero-copy">
          <div class="hero-orbs" aria-hidden="true">
            <span class="hero-orb orb-one"></span>
            <span class="hero-orb orb-two"></span>
          </div>
          <p class="eyebrow">Instant browser play</p>
          <h1>A dark arcade library built for faster discovery and cleaner play.</h1>
          <p class="hero-text">
            A tighter front page, sharper hierarchy, and a more cinematic black palette make it easier
            to browse, compare, and jump into the next game without friction.
          </p>
          <div class="hero-actions">
            <a class="primary-action" href="#game-grid">
              <span class="action-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </span>
              Browse Collection
            </a>
            <span class="hero-note">
              <span class="note-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v18M3 12h18"/></svg>
              </span>
              No install · Instant launch
            </span>
          </div>
          <div class="hero-stats">
            <div class="stat-card">
              <span class="stat-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h10"/></svg>
              </span>
              <strong>{{ gameCount }}</strong>
              <span>Playable titles</span>
            </div>
            <div class="stat-card">
              <span class="stat-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 4 2.5 5 5.5.8-4 3.8.9 5.4L12 16.7 7.1 19l.9-5.4-4-3.8 5.5-.8Z"/></svg>
              </span>
              <strong>{{ hotCount }}</strong>
              <span>Trending picks</span>
            </div>
            <div class="stat-card">
              <span class="stat-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
              </span>
              <strong>{{ newCount }}</strong>
              <span>Fresh drops</span>
            </div>
          </div>
        </div>

        <div class="hero-feature" v-if="featuredGame">
          <div class="feature-art">
            <img :src="featuredGame.image" :alt="featuredGame.name" />
          </div>
          <div class="feature-body">
            <div class="feature-topline">
              <span class="pill pill-hot">{{ featuredGame.flag || 'featured' }}</span>
              <span>{{ featuredGame.votes }} votes</span>
            </div>
            <h2>{{ featuredGame.name }}</h2>
            <p>{{ cleanDesc(featuredGame.desc) }}</p>
            <router-link
              class="feature-link"
              :to="{ name: 'Game', params: { slug: featuredGame.slug } }"
            >
              Launch Featured Title
            </router-link>
          </div>
        </div>
      </section>
    </header>

    <main class="content-shell">
      <div class="section-line" aria-hidden="true"></div>
      <section class="section-head">
        <div>
          <p class="section-kicker">Curated library</p>
          <h2 id="game-grid">Pick a title and drop straight into play.</h2>
        </div>
        <div class="section-caption">Editorial layout, faster scanning, one-click entry.</div>
      </section>

      <section class="game-grid">
        <article class="game-card" v-for="item in gameList" :key="item.id">
          <router-link class="game-card-link" :to="{ name: 'Game', params: { slug: item.slug } }">
            <span class="sr-only">Play {{ item.name }}</span>
          </router-link>
          <div class="game-thumb">
            <img :src="item.image" :alt="item.name" />
            <span v-if="item.flag" class="pill" :class="item.flag === 'hot' ? 'pill-hot' : 'pill-new'">
              {{ item.flag }}
            </span>
          </div>
          <div class="game-card-body">
            <div class="game-card-top">
              <h3>{{ item.name }}</h3>
              <span>{{ item.votes }} votes</span>
            </div>
            <p>{{ shortDesc(item.desc) }}</p>
            <div class="game-card-footer">
              <span class="footer-chip">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h14v16H5Zm3 3v10h8V7Z"/></svg>
                HTML5
              </span>
              <span class="footer-chip">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                Open now
              </span>
            </div>
          </div>
        </article>
      </section>
    </main>

    <game-footer />
  </div>
</template>

<script>
import GameFooter from "../components/GameFooter.vue"
import { cleanDesc, getGameList } from "@/common/games"

export default {
  name: "Main",
  components: { GameFooter },
  computed: {
    gameList() {
      return getGameList()
    },
    featuredGame() {
      return this.gameList.find(item => item.flag === "hot") || this.gameList[0]
    },
    gameCount() {
      return this.gameList.length
    },
    hotCount() {
      return this.gameList.filter(item => item.flag === "hot").length
    },
    newCount() {
      return this.gameList.filter(item => item.flag === "new").length
    },
    featuredLabel() {
      if (!this.featuredGame) {
        return "Curated games"
      }
      return this.featuredGame.name
    }
  },
  methods: {
    cleanDesc(desc) {
      return cleanDesc(desc)
    },
    shortDesc(desc) {
      var normalized = this.cleanDesc(desc)
      return normalized.slice(0, 92) + (normalized.length > 92 ? "..." : "")
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  color: #f3f4f6;
  background:
    radial-gradient(circle at top left, rgba(255, 92, 92, 0.18), transparent 32%),
    radial-gradient(circle at 85% 10%, rgba(67, 214, 255, 0.12), transparent 24%),
    linear-gradient(180deg, #060709 0%, #0b0d12 42%, #050505 100%);
}

.hero-shell,
.content-shell {
  width: min(1240px, calc(100% - 40px));
  margin: 0 auto;
}

.hero-shell {
  padding: 28px 0 36px;
}

.hero-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 34px;
  padding: 14px 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  background: rgba(9, 10, 14, 0.72);
  backdrop-filter: blur(18px);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: #f9fafb;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fafafa, #747474);
  color: #050505;
  font: 700 20px/1 "Trebuchet MS", "Arial Black", sans-serif;
  box-shadow: 0 12px 28px rgba(255, 255, 255, 0.18);
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.brand-copy strong {
  font: 700 22px/1.1 "Trebuchet MS", "Arial Black", sans-serif;
  letter-spacing: 0.04em;
}

.brand-copy em,
.hero-nav-meta,
.feature-topline,
.section-kicker,
.section-caption,
.game-card-top span,
.game-card-footer {
  color: rgba(241, 245, 249, 0.7);
  font: 500 12px/1.4 "Courier New", monospace;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.hero-nav-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(340px, 0.85fr);
  gap: 24px;
  align-items: stretch;
}

.hero-copy,
.hero-feature {
  position: relative;
  overflow: hidden;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(17, 19, 24, 0.92), rgba(6, 7, 9, 0.96));
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.38);
  animation: panelReveal 0.8s ease both;
}

.hero-copy {
  padding: 44px;
  isolation: isolate;
}

.hero-orbs {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(6px);
  opacity: 0.6;
}

.orb-one {
  top: 18%;
  right: 10%;
  width: 84px;
  height: 84px;
  background: radial-gradient(circle, rgba(255, 164, 115, 0.35), transparent 68%);
  animation: floatOrb 6.5s ease-in-out infinite;
}

.orb-two {
  right: 26%;
  bottom: 14%;
  width: 58px;
  height: 58px;
  background: radial-gradient(circle, rgba(117, 222, 255, 0.28), transparent 68%);
  animation: floatOrb 8s ease-in-out infinite reverse;
}

.hero-copy::before,
.hero-feature::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), transparent 36%),
    linear-gradient(0deg, transparent, rgba(255, 255, 255, 0.02));
  pointer-events: none;
}

.hero-copy::after {
  content: "";
  position: absolute;
  right: -80px;
  bottom: -80px;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.12), transparent 62%);
  opacity: 0.45;
  z-index: -1;
}

.eyebrow {
  margin-bottom: 14px;
  color: #ff7a59;
  font: 700 12px/1 "Courier New", monospace;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 10em;
  margin-bottom: 18px;
  font: 700 clamp(38px, 5vw, 76px)/0.95 Georgia, "Times New Roman", serif;
  letter-spacing: -0.04em;
}

.hero-text,
.feature-body p,
.game-card-body p {
  color: rgba(226, 232, 240, 0.78);
  font-size: 16px;
  line-height: 1.75;
}

.hero-text {
  max-width: 40rem;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin: 28px 0 34px;
}

.primary-action,
.feature-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f6f6f6, #7b8794);
  color: #060709;
  font: 700 14px/1 "Trebuchet MS", "Arial Black", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 12px 30px rgba(255, 255, 255, 0.18);
}

.action-icon,
.note-icon,
.stat-icon,
.footer-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-icon,
.note-icon,
.stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.primary-action .action-icon {
  background: rgba(6, 7, 9, 0.1);
}

.action-icon svg,
.note-icon svg,
.stat-icon svg,
.footer-chip svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.primary-action:hover,
.feature-link:hover {
  color: #060709;
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(255, 255, 255, 0.22);
}

.hero-note {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(241, 245, 249, 0.72);
  font-size: 14px;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.stat-card {
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.stat-icon {
  margin-bottom: 12px;
}

.stat-card strong {
  display: block;
  margin-bottom: 8px;
  font: 700 28px/1 Georgia, serif;
}

.stat-card span {
  color: rgba(226, 232, 240, 0.72);
  font-size: 13px;
}

.hero-feature {
  display: grid;
  grid-template-rows: 320px auto;
}

.feature-art {
  position: relative;
  overflow: hidden;
}

.feature-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.05) contrast(1.05);
  transform: scale(1.02);
}

.feature-body {
  position: relative;
  z-index: 1;
  padding: 28px;
}

.feature-body::after {
  content: "";
  position: absolute;
  inset: auto 28px 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.14), transparent);
}

.feature-topline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.feature-body h2,
.section-head h2 {
  font: 700 clamp(28px, 3.2vw, 42px)/1.05 Georgia, serif;
  letter-spacing: -0.03em;
}

.feature-body p {
  margin: 16px 0 22px;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  font: 700 11px/1 "Courier New", monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.pill-hot {
  background: rgba(255, 112, 76, 0.16);
  color: #ff8b6a;
  border: 1px solid rgba(255, 112, 76, 0.35);
}

.pill-new {
  background: rgba(111, 255, 179, 0.14);
  color: #90ffbf;
  border: 1px solid rgba(111, 255, 179, 0.28);
}

.content-shell {
  padding-bottom: 48px;
}

.section-line {
  height: 1px;
  margin: 0 0 22px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.02), transparent);
}

.section-head {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.section-kicker {
  margin-bottom: 10px;
}

.game-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.game-card {
  position: relative;
  overflow: hidden;
  min-height: 100%;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(17, 19, 24, 0.95), rgba(7, 8, 10, 0.98));
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.28);
  transition: transform 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease;
  animation: cardReveal 0.7s ease both;
}

.game-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06), transparent 35%);
  opacity: 0;
  transition: opacity 0.28s ease;
  pointer-events: none;
}

.game-card:hover {
  transform: translateY(-6px);
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 26px 42px rgba(0, 0, 0, 0.4);
}

.game-card:hover::before {
  opacity: 1;
}

.game-card::after {
  content: "";
  position: absolute;
  inset: -20% auto auto -10%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.08), transparent 70%);
  transform: translate3d(-40px, -40px, 0);
  transition: transform 0.35s ease, opacity 0.35s ease;
  opacity: 0;
  pointer-events: none;
}

.game-card:hover::after {
  transform: translate3d(0, 0, 0);
  opacity: 1;
}

.game-card-link {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.game-thumb {
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
}

.game-thumb::after {
  content: "";
  position: absolute;
  inset: auto 0 0;
  height: 45%;
  background: linear-gradient(180deg, transparent, rgba(4, 5, 7, 0.9));
}

.game-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.game-card:hover .game-thumb img {
  transform: scale(1.06);
}

.game-thumb .pill {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 1;
}

.game-card-body {
  padding: 18px;
  position: relative;
}

.game-card-top {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.game-card-top h3 {
  font: 700 20px/1.15 Georgia, serif;
}

.game-card-body p {
  min-height: 78px;
  font-size: 14px;
  line-height: 1.65;
}

.game-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-chip {
  gap: 6px;
}

.footer-chip svg {
  width: 14px;
  height: 14px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.game-card:nth-child(2n) {
  animation-delay: 0.05s;
}

.game-card:nth-child(3n) {
  animation-delay: 0.1s;
}

.game-card:nth-child(4n) {
  animation-delay: 0.15s;
}

@keyframes panelReveal {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardReveal {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes floatOrb {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, -10px, 0);
  }
}

@media (max-width: 1100px) {
  .hero-grid {
    grid-template-columns: 1fr;
  }

  .game-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 780px) {
  .hero-shell,
  .content-shell {
    width: min(100%, calc(100% - 24px));
  }

  .hero-shell {
    padding-top: 14px;
  }

  .hero-nav,
  .section-head,
  .hero-actions,
  .game-card-top,
  .game-card-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-copy {
    padding: 28px 22px;
  }

  .hero-orbs {
    display: none;
  }

  .hero-copy h1 {
    max-width: none;
    font-size: clamp(34px, 11vw, 52px);
    line-height: 0.98;
  }

  .hero-stats,
  .game-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-feature {
    grid-template-rows: 240px auto;
  }

  .primary-action,
  .feature-link {
    width: 100%;
    min-height: 56px;
  }

  .hero-note,
  .hero-text,
  .feature-body p,
  .game-card-body p {
    font-size: 15px;
  }

  .game-card {
    border-radius: 20px;
  }

  .game-card-body {
    padding: 16px;
  }
}

@media (max-width: 520px) {
  .hero-nav {
    padding: 14px;
    border-radius: 18px;
  }

  .brand {
    gap: 12px;
  }

  .brand-mark {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    font-size: 18px;
  }

  .hero-copy,
  .hero-feature,
  .feature-body {
    border-radius: 22px;
  }

  .hero-stats,
  .game-grid {
    grid-template-columns: 1fr;
  }

  .hero-copy {
    padding: 24px 18px;
  }

  .hero-actions {
    width: 100%;
    gap: 12px;
    margin: 22px 0 26px;
  }

  .hero-stats {
    gap: 10px;
  }

  .stat-card {
    padding: 16px;
  }

  .feature-art {
    min-height: 220px;
  }

  .feature-body {
    padding: 20px 18px 22px;
  }

  .section-head {
    margin-bottom: 16px;
  }

  .section-head h2 {
    font-size: 30px;
  }

  .game-card-top h3 {
    font-size: 18px;
  }

  .game-card-body p {
    min-height: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Main.vue